import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, getToken } from "@/lib/api";
import {
  contractFixturesQueryOptions,
  contractKeys,
  contractsListQueryOptions,
} from "@/lib/queries";
import { ContractsGrid } from "@/grids/ContractsGrid";
import { useToast } from "@/components/Toaster";
import type { AgentDecision } from "@/lib/types";

export const Route = createFileRoute("/_auth/contracts/")({
  loader: ({ context }) =>
    (context as { queryClient: ReturnType<typeof useQueryClient> } | undefined)
      ?.queryClient.ensureQueryData(contractsListQueryOptions()),
  component: ContractsListPage,
});


/**
 * Upload progress stages. The backend /api/contracts/upload is a single
 * synchronous endpoint that (a) saves the PDF, (b) calls Claude Vision
 * to extract structured terms, (c) creates the Contract + BillingTerms
 * + Subscriptions + initial invoices. From the frontend we can't
 * actually observe those sub-stages happening — we just know the
 * request is in flight for ~15-30s — so we fake a staged progression
 * timed on elapsed seconds. Feels much less like a dead spinner.
 */
const STAGES = [
  { at: 0,  label: "Uploading PDF…",                    emoji: "📤" },
  { at: 2,  label: "Extracting contract terms with AI…", emoji: "🤖" },
  { at: 12, label: "Creating billing plan + subscription…",  emoji: "📋" },
  { at: 22, label: "Generating initial invoices…",           emoji: "🧾" },
  { at: 30, label: "Almost done…",                           emoji: "✨" },
];


function ContractsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery(contractsListQueryOptions());
  const fixtures = useQuery(contractFixturesQueryOptions());
  const [chosen, setChosen] = useState<string>("delta_building_centers");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload progress state
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadElapsed, setUploadElapsed] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<AgentDecision | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Tick elapsed seconds while uploading — drives the stage progression.
  useEffect(() => {
    if (!uploading) return;
    const started = Date.now();
    const id = setInterval(() => {
      setUploadElapsed(Math.floor((Date.now() - started) / 1000));
    }, 500);
    return () => clearInterval(id);
  }, [uploading]);

  const currentStage = (() => {
    let s = STAGES[0];
    for (const st of STAGES) {
      if (uploadElapsed >= st.at) s = st;
    }
    return s;
  })();

  const onSuccess = async (decision: AgentDecision) => {
    toast.success(
      `Ingested. Created ${decision.plans_created.length} plan(s), ` +
        `${decision.subscriptions_created.length} sub(s), ` +
        `${decision.initial_invoices_generated.length} invoice(s), ` +
        `${decision.events_emitted} events.`,
    );
    await qc.invalidateQueries({ queryKey: contractKeys.all });
    setTimeout(
      () =>
        navigate({
          to: "/contracts/$contractId",
          params: { contractId: decision.contract_id },
        }),
      400,
    );
  };

  const ingestMutation = useMutation({
    mutationFn: (fixture: string) =>
      api<AgentDecision>("/api/contracts/ingest-fixture", {
        method: "POST",
        body: { fixture },
      }),
    onSuccess,
    onError: (err: Error) => toast.error(`Error: ${err.message}`),
  });

  const uploadPdf = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please pick a .pdf file");
      return;
    }
    // Reset any prior upload state
    setUploadError(null);
    setUploadSuccess(null);
    setUploadFile(file);
    setUploadElapsed(0);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      // Use raw fetch — ofetch's body wrapping would clobber the multipart
      const r = await fetch("/api/contracts/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
        body: fd,
      });
      if (!r.ok) {
        // Read the body ONCE — a Response stream can only be consumed a
        // single time. Read as text, then try to parse it as JSON; falling
        // back to a second read (r.text() after r.json()) throws
        // "body stream already read" and masks the real error.
        const raw = await r.text();
        let detail = raw;
        try {
          const j = JSON.parse(raw);
          detail = j.detail ?? j.message ?? raw;
        } catch {
          // Body wasn't JSON — keep the raw text as the detail.
        }
        throw new Error(detail || `${r.status} ${r.statusText}`);
      }
      const decision = (await r.json()) as AgentDecision;
      setUploadSuccess(decision);
      await onSuccess(decision);
    } catch (e) {
      const msg = (e as Error).message;
      setUploadError(msg);
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4 dark:bg-gray-900 dark:border-gray-800">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Contracts</h1>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {/* Real PDF upload — Contract Agent runs on it */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadPdf(f);
            }}
          />
          <button
            disabled={uploading || ingestMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
            className="text-sm font-medium bg-blue-600 text-white rounded-md px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
            title="Upload a signed contract PDF — Contract Agent extracts billing terms automatically"
          >
            {uploading ? "Uploading…" : "📄 Upload Contract PDF"}
          </button>

          <span className="text-xs text-slate-400 mx-2 dark:text-slate-500">or</span>

          {/* Fixture dropdown — for the canned demo flow */}
          <select
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
            className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
            disabled={uploading || ingestMutation.isPending}
          >
            {(fixtures.data?.fixtures ?? ["delta_building_centers"]).map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <button
            disabled={uploading || ingestMutation.isPending}
            onClick={() => ingestMutation.mutate(chosen)}
            className="text-sm font-medium bg-slate-200 text-slate-700 rounded-md px-3 py-1.5 hover:bg-slate-300 disabled:opacity-50 dark:bg-gray-700 dark:text-slate-300"
          >
            {ingestMutation.isPending ? "Ingesting…" : "Use fixture"}
          </button>
        </div>
      </div>

      {/* Upload progress banner — sticky below header while uploading */}
      {uploading && uploadFile && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
          <div className="flex items-center gap-4 max-w-5xl">
            <div className="text-2xl">{currentStage.emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-3">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {currentStage.label}
                </span>
                <span className="text-xs text-slate-500 font-mono dark:text-slate-400">
                  {uploadElapsed}s elapsed
                </span>
                {uploadElapsed > 45 && (
                  <span className="text-xs text-amber-700">
                    (taking longer than usual — the AI may be rate-limited)
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-600 mt-0.5 truncate dark:text-slate-300">
                {uploadFile.name} · {formatFileSize(uploadFile.size)}
              </div>
              {/* Animated indeterminate progress stripe */}
              <div className="mt-2 h-1.5 bg-blue-200 rounded-full overflow-hidden relative">
                <div
                  className="h-full bg-blue-600 rounded-full"
                  style={{
                    width: `${Math.min(95, 10 + uploadElapsed * 3)}%`,
                    transition: "width 500ms linear",
                  }}
                />
              </div>
              {/* Stage dots */}
              <div className="mt-1.5 flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                {STAGES.map((s, i) => (
                  <span
                    key={i}
                    className={
                      uploadElapsed >= s.at
                        ? "text-blue-700 font-medium"
                        : "text-slate-400"
                    }
                  >
                    {uploadElapsed >= s.at ? "●" : "○"} {s.label.replace("…", "")}
                    {i < STAGES.length - 1 && (
                      <span className="mx-1 text-slate-300">·</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success banner — sticky green card with links, dismissible */}
      {!uploading && uploadSuccess && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3">
          <div className="flex items-start gap-3 max-w-5xl">
            <div className="text-xl">✓</div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-emerald-900">
                Contract ingested successfully
                {uploadFile && (
                  <span className="font-normal text-emerald-700 ml-2">
                    ({uploadFile.name})
                  </span>
                )}
              </div>
              <div className="text-xs text-emerald-800 mt-0.5">
                Created {uploadSuccess.plans_created.length} plan(s),{" "}
                {uploadSuccess.subscriptions_created.length} subscription(s),{" "}
                {uploadSuccess.initial_invoices_generated.length} invoice(s),{" "}
                {uploadSuccess.events_emitted} event(s).
              </div>
            </div>
            <button
              onClick={() => { setUploadSuccess(null); setUploadFile(null); }}
              className="text-xs text-emerald-700 hover:text-emerald-900"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Error banner — red, sticky, dismissible */}
      {!uploading && uploadError && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3">
          <div className="flex items-start gap-3 max-w-5xl">
            <div className="text-xl">⚠</div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-red-900">
                Upload failed
                {uploadFile && (
                  <span className="font-normal text-red-700 ml-2">
                    ({uploadFile.name})
                  </span>
                )}
              </div>
              <div className="text-xs text-red-800 mt-0.5 font-mono break-all">
                {uploadError}
              </div>
            </div>
            <button
              onClick={() => { setUploadError(null); setUploadFile(null); }}
              className="text-xs text-red-700 hover:text-red-900"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Drop zone wraps the list — dragging a PDF onto the page uploads. */}
      <div
        className={
          "flex-1 overflow-hidden px-6 py-4 " +
          (isDraggingOver ? "bg-blue-50" : "")
        }
        onDragOver={(e) => {
          e.preventDefault();
          if (!uploading) setIsDraggingOver(true);
        }}
        onDragLeave={() => setIsDraggingOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDraggingOver(false);
          if (uploading) return;
          const f = e.dataTransfer.files?.[0];
          if (f) void uploadPdf(f);
        }}
      >
        {isDraggingOver && (
          <div className="border-2 border-blue-500 border-dashed rounded-lg p-8 text-center bg-blue-50">
            <div className="text-blue-700 font-semibold">
              Drop PDF to upload
            </div>
          </div>
        )}
        {!isDraggingOver && (
          <>
            {list.isLoading && <div className="text-sm text-slate-500 dark:text-slate-400">Loading contracts…</div>}
            {list.error && <div className="text-sm text-red-600">Error: {(list.error as Error).message}</div>}
            {list.data && list.data.length === 0 && !uploading && (
              <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center dark:border-gray-700">
                <div className="text-slate-500 text-sm dark:text-slate-400">
                  No contracts yet. Drop a PDF anywhere on this page,
                </div>
                <div className="text-slate-400 text-xs mt-1 dark:text-slate-500">
                  click the Upload button, or load the {chosen} fixture.
                </div>
              </div>
            )}
            {list.data && list.data.length > 0 && <ContractsGrid contracts={list.data} />}
          </>
        )}
      </div>
    </div>
  );
}
